import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { personalInfo } from '../data/mock';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Clock,
  ArrowRight,
  MessageCircle,
  Instagram
} from 'lucide-react';

const Contact = () => {
  const contactOptions = [
    {
      icon: MessageCircle,
      title: "1:1 Personal Brand Audit",
      description: "30 min call to analyze your brand, identify opportunities, and create an actionable roadmap for growth",
      action: "Book Your Audit",
      highlight: "Most Popular",
      link: "https://calendly.com/simmbedi-sb/15-min-discovery-call"
    }
  ];

  return (
    <section className="py-16 px-6" id="contact">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Let's create something unforgettable.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Whether you need content that blends into feeds or a strategy that sets you apart , let's talk !
          </p>
        </div>

        {/* Contact Options */}
        <div className="flex justify-center mb-16">
          <div className="max-w-md">
          {contactOptions.map((option, index) => {
            const IconComponent = option.icon;
            return (
              <Card key={index} className="group text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer">
                <CardContent className="p-8">
                  {/* Add image at the top */}
                  <div className="mb-6">
                    <div className="relative w-32 h-32 mx-auto rounded-2xl overflow-hidden">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_identity-forge-17/artifacts/jaosoffa_IMG_3458.png"
                        alt="Personal Brand Audit"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  <div className="relative mb-6">
                    <div 
                      className="w-16 h-16 rounded-full mx-auto flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                      style={{
                        background: `linear-gradient(135deg, #ffe6fe 0%, #fdecff 100%)`
                      }}
                    >
                      <IconComponent className="h-8 w-8 text-gray-700" />
                    </div>
                    
                    <Badge 
                      className="absolute -top-2 -right-2 bg-pink-100 text-pink-800"
                    >
                      {option.highlight}
                    </Badge>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {option.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {option.description}
                  </p>
                  
                  <Button className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2 rounded-full group-hover:scale-105 transition-all duration-300">
                    <a href={option.link} target="_blank" rel="noopener noreferrer" className="flex items-center">
                      {option.action}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Instagram Connect */}
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="space-y-6">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                    Let's Connect Here
                  </h3>
                  <p className="text-gray-600">
                    Follow my journey and get behind-the-scenes content creation tips ✨
                  </p>
                </div>
                
                <div 
                  className="p-6 rounded-2xl"
                  style={{
                    background: `linear-gradient(135deg, #ffe6fe 0%, #fdecff 50%, #dee4d0 100%)`
                  }}
                >
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <Instagram className="h-8 w-8 text-gray-700" />
                    <span className="text-xl font-semibold text-gray-900">@vibewithsimm</span>
                  </div>
                  
                  {/* Single Image */}
                  <div className="mb-6">
                    <div className="relative rounded-xl overflow-hidden max-w-md mx-auto">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_identity-forge-17/artifacts/shuyopmz_IMG_8084.jpg"
                        alt="Simm behind the scenes"
                        className="w-full h-40 object-cover"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    size="lg"
                    className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                  >
                    <a href="https://www.instagram.com/vibewithsimm?igsh=OW0xcmhkNWUxcHRz&utm_source=qr" target="_blank" rel="noopener noreferrer" className="flex items-center">
                      Follow on Instagram
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Contact Details Card */}
            <Card 
              className="border-0 shadow-xl"
              style={{
                background: `linear-gradient(135deg, #ffe6fe 0%, #fdecff 50%, #dee4d0 100%)`
              }}
            >
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_identity-forge-17/artifacts/swvr01jx_DSC06764.JPG" 
                    alt="Simm Bedi"
                    className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
                  />
                  <div>
                    <h3 className="text-2xl font-semibold text-gray-900">
                      Get In Touch
                    </h3>
                    <p className="text-gray-700 text-sm">Ready to collaborate?</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full">
                      <Mail className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Email</div>
                      <div className="text-gray-700">{personalInfo.contact.email}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full">
                      <Phone className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Phone</div>
                      <div className="text-gray-700">{personalInfo.contact.phone}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full">
                      <MapPin className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Location</div>
                      <div className="text-gray-700">Toronto, Canada</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full">
                      <Clock className="h-5 w-5 text-gray-700" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">Response Time</div>
                      <div className="text-gray-700">Within 24 hours</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Availability Card */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Current Availability
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    <span className="text-gray-700">Accepting new projects</span>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg mt-4">
                    <p className="text-sm text-green-800">
                      <strong>Limited spots available!</strong> Book your consultation soon to secure your project slot.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;